import { api } from './api'

// ---- Bookmarks -------------------------------------------------------------

// The current user's bookmarks (newest first). Optional { topic } narrows to a
// single topic id. Resolves to an array of bookmarks (item + topic names
// populated).
export function listBookmarks({ topic, nested } = {}) {
  const params = new URLSearchParams()
  if (topic) params.set('topic', topic)
  if (nested) params.set('nested', '1')
  const query = params.toString()
  return api.get(`/bookmarks${query ? `?${query}` : ''}`).then((data) => data.bookmarks)
}

// Bookmark an item. Idempotent on the server: re-bookmarking merges any topics
// and returns the existing bookmark. Expects { item, topics? } (ids). Resolves
// to the bookmark.
export function createBookmark(payload) {
  return api.post('/bookmarks', payload).then((data) => data.bookmark)
}

// Replace a bookmark's topic membership. Accepts { topics } (an array of topic
// ids). Resolves to the updated bookmark.
export function updateBookmark(id, payload) {
  return api.patch(`/bookmarks/${id}`, payload).then((data) => data.bookmark)
}

// Remove a bookmark. Resolves to the deleted bookmark.
export function deleteBookmark(id) {
  return api.del(`/bookmarks/${id}`).then((data) => data.bookmark)
}

// ---- Topics ----------------------------------------------------------------

// The current user's topics (alphabetical, with bookmark counts). Resolves to an
// array.
export function listTopics() {
  return api.get('/topics').then((data) => data.topics)
}

// Create a topic. Expects { name, parentId? }. A parentId nests the topic under
// that topic. Resolves to the topic.
export function createTopic(payload) {
  return api.post('/topics', payload).then((data) => data.topic)
}

// Rename a topic. Expects { name }. Resolves to the updated topic.
export function updateTopic(id, payload) {
  return api.patch(`/topics/${id}`, payload).then((data) => data.topic)
}

// Move a topic under a new parent. parentId null places it at the top level.
// Topics inside it move with it. Resolves to the topics whose path changed.
export function moveTopic(id, parentId) {
  return api.post(`/topics/${id}/move`, { parentId: parentId || null }).then((data) => data.topics || [])
}

// Delete a topic (bookmarks are kept, just unfiled). Resolves to the topic.
export function deleteTopic(id) {
  return api.del(`/topics/${id}`).then((data) => data.topic)
}

// Duplicate a topic: makes an empty "<name> (copy)" (name only — bookmarks are
// not copied). Resolves to the new topic.
export function duplicateTopic(id) {
  return api.post(`/topics/${id}/duplicate`).then((data) => data.topic)
}

// Delete several topics at once (bookmarks kept). Resolves to { deleted }.
export function bulkDeleteTopics(ids) {
  return api.post('/topics/bulk-delete', { ids })
}
